import { Address } from '@vsolv/core/address/domain';

/**
 * Generic warranty declaration data structure
 * Can be customized per tenant/provider as needed
 */
export interface DeclarationData extends Record<string, unknown> {
  // Provider branding
  logoUrl?: string | null;

  // Agreement Information
  agreementNumber: string | null;
  contractNumber: string;
  policyNumber: string | null;
  policyName: string;
  planName: string;
  basePlanName: string;
  termInMonths: number;
  purchaseDate: Date | null;
  effectiveDate: Date;
  renewalDate: string | null;
  expirationDate: Date | null;
  deductible: string;
  purchasePrice: number;

  // Agreement Holder Information (Customer)
  customerName: string;
  customerPhone: string | null;
  customerAddress: Address.Model | null;

  // Covered asset — driven by the asset's property set, so it works for any asset type
  coveredAsset: {
    // The asset type (its property set's name); shown as the section heading
    assetType: string;
    // The asset's answers, already formatted as label/value pairs
    fields: { label: string; value: string }[];
  };

  // Optional metadata for specialized declaration variants
  propertyAddress?: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    zip: string;
  } | null;
  isUnder5000SqFt?: boolean | null;

  // Seller Information (Distributor)
  sellerName: string | null;
  sellerPhone: string | null;
  sellerAddress: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    zip: string;
  } | null;

  // Coverage Selection
  coverageOption: 'Appliance' | 'Systems' | 'Total' | string;
  selectedOptions: string[];

  // Coverage Table
  coverages: CoverageRow[];
  coverageSummary: CoverageSummary;

  // Tenant/Provider-specific configuration
  tenantConfig?: TenantDeclarationConfig;
}

export interface CoverageRow {
  coverageName: string;
  coveredUnderServicePlan: boolean;
  liabilityLimitUnder10Years: number | null;
  liabilityLimit10YearsOrOlder: number | null;
}

export interface CoverageSummary {
  layout: 'portrait' | 'landscape';
  plans: CoverageSummaryPlan[];
  rows: CoverageSummaryRow[];
}

export interface CoverageSummaryPlan {
  id: string;
  name: string;
  description: string | null;
  isSelected: boolean;
}

export interface CoverageSummaryRow {
  coverageId: string;
  coverageName: string;
  group: string | null;
  values: CoverageSummaryValue[];
}

export interface CoverageSummaryValue {
  planId: string;
  isSelected: boolean;
  status: 'included' | 'optional' | 'excluded';
  note: string | null;
  liabilityLimit: number | null;
}

/**
 * Tenant-specific configuration for declaration page
 * Allows customization of footer text and other tenant-specific details
 */
export interface TenantDeclarationConfig {
  providerName?: string;
  primaryColor?: string | null;
  accentColor?: string | null;
  providerEmail?: string | null;
  providerPhone?: string | null;
  providerAddress?: Address.Model | null;
  page1Footer?: string;
  coverageTableFooter?: string;
  page2Footer?: string;
  customFields?: Record<string, unknown>;
}

/**
 * Default declaration configuration for HWG (Headstart Warranty Group)
 * Can be used as a template for other tenants
 */
export const DEFAULT_HWG_CONFIG: TenantDeclarationConfig = {
  providerName: 'Headstart Warranty Group',
  primaryColor: null,
  accentColor: null,
  providerEmail: null,
  providerPhone: null,
  providerAddress: null,
  page1Footer: '',
  coverageTableFooter: '',
  page2Footer: '',
};
